import React, { useRef } from 'react';
import { Briefcase, Palette, PenTool, Layout, BookOpen, Share2, Monitor } from 'lucide-react';
import styles from './Experience.module.css';

const experiences = [
  {
    year: '2024',
    role: 'Senior Graphic Designer',
    company: 'Creative Studio',
    desc: 'Leading design projects for major brands, creating comprehensive visual identities and marketing campaigns.',
    tags: ['Branding', 'UI/UX', 'Print'],
    icon: <PenTool className="w-5 h-5" />,
    color: '#3b82f6',
  },
  {
    year: '2023',
    role: 'Visual Designer',
    company: 'Tech Startup',
    desc: 'Designed user interfaces and marketing materials, collaborated with development teams on product design.',
    tags: ['UI Design', 'Figma', 'Web'],
    icon: <Palette className="w-5 h-5" />,
    color: '#10b981',
  },
  {
    year: '2022',
    role: 'Freelance Designer',
    company: 'Various Clients',
    desc: 'Worked with diverse clients on branding, illustration, and digital design projects across multiple industries.',
    tags: ['Illustration', 'Branding', 'Freelance'],
    icon: <Briefcase className="w-5 h-5" />,
    color: '#f59e0b',
  },
  {
    year: '2021',
    role: 'UI/UX',
    company: 'Design Agency',
    desc: 'Designed user-centric interfaces for mobile and web applications, improving usability and increasing customer engagement.',
    tags: ['User Research', 'Wireframing', 'Prototyping'],
    icon: <Layout className="w-5 h-5" />,
    color: '#b5ec48',
  },
  {
    year: '2021',
    role: 'Graphic Designer',
    company: 'Design Agency',
    desc: 'Developed creative assets for advertising campaigns, social media, and corporate branding projects.',
    tags: ['Social Media Design', 'Illustration', 'Typography'],
    icon: <PenTool className="w-5 h-5" />,
    color: '#4858ec',
  },
  {
    year: '2021',
    role: 'Book Illustrator',
    company: 'Design Agency',
    desc: 'Creates compelling visual narratives that bring stories to life through expressive and engaging illustrations.',
    tags: ['Digital Art', 'Vector Illustration', 'Visual Storytelling'],
    icon: <BookOpen className="w-5 h-5" />,
    color: '#ecb048',
  },
  {
    year: '2021',
    role: 'Social Media Support Engineer',
    company: 'Design Agency',
    desc: 'Provides reliable technical support for social media platforms, ensuring smooth performance and quick resolution of user issues.',
    tags: ['Technical Support', 'Platform Operations', 'Online Services'],
    icon: <Share2 className="w-5 h-5" />,
    color: '#ec485b',
  },
  {
    year: '2020',
    role: 'Deck Designer',
    company: 'Design Studio',
    desc: 'Designs high-impact presentation decks that turn ideas into clear, compelling visual stories.',
    tags: ['Print Design', 'Layout', 'Adobe Suite'],
    icon: <Monitor className="w-5 h-5" />,
    color: '#06b6d4',
  },
];

const Experience = () => {
  const ref = useRef(null);

  return (
    <section ref={ref} id="experience" className={styles.experienceSection}>
      {/* Animated BG */}
      <div className={styles.experienceBg} />
      <div className={styles.experienceParticles}>
        {Array.from({ length: 20 }).map((_, i) => (
          <div key={i} className={styles.experienceParticle} style={{ '--d': `${i * 0.7}s` }} />
        ))}
      </div>

      <div className={styles.experienceContainer}>
        <header className={styles.experienceHeader}>
          <span className={styles.experienceLabel}>Timeline</span>
          <h2 className={styles.experienceTitle}>Experience</h2>
          <div className={styles.experienceLine} />
        </header>

        <div className={styles.experienceScrollWrapper}>
          <div className={styles.experienceCards}>
            {experiences.map((exp, i) => (
              <article
                key={i}
                className={styles.experienceCard}
                style={{ '--c': exp.color }}
              >
                <div className={styles.experienceCardGlow} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <div className={styles.experienceIcon}>{exp.icon}</div>
                </div>
                <h3 className={styles.experienceRole}>{exp.role}</h3>
                <p className={styles.experienceDesc}>{exp.desc}</p>
                <div className={styles.experienceTags}>
                  {exp.tags.map((t, ti) => (
                    <span key={ti} className={styles.experienceTag}>{t}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
